/**
 * Who may see the trading platform (the Vertex brokerage pages, the Trading menu entry and the
 * buying-power strip). This file is the single answer; every surface asks it.
 *
 * THE RULE. Trading is shown only when ALL of these hold:
 *
 *   1. The build has trading switched on: `NEXT_PUBLIC_TRADING_ENABLED === "1"`, inlined at
 *      build time. It is set on every Vercel Preview deployment (ted-beta1..4,
 *      sandbox.trading.ortex.dev, MR previews). Production never sets it, and `vercel.sh`
 *      refuses to build production if it is set, so app.ortex.com cannot show trading whatever
 *      the user's entitlements are.
 *   2. Not the native app (`isOrtexApp()`): trading stays out of the store builds for now.
 *   3. TED says the user may use trading: `trading_platform_access === true` on `/API/v2/user`
 *      (TED `OrtexUser.can_use_trading_platform`, ortex/ted/ted#2408). TED owns the whole rule:
 *      today the hand-assigned `PRODUCT_TRADING_PLATFORM` group AND staff, while TED's
 *      `TRADING_PLATFORM_REQUIRES_STAFF` is set. Opening trading to non-staff group members is
 *      that one TED constant; this file does not change, which is why it reads the combined
 *      answer rather than `is_staff` and `trading_platform` (raw membership) itself.
 *
 * The user fact comes from TED; nothing here can be satisfied by the browser alone. Vertex
 * asks TED the same question (vertex#96), so the GUI and the brokerage API agree.
 *
 * FAILING CLOSED. Anything other than a literal `true` is a no: a missing key (a backend that
 * predates the flag, or the payload's early-return branches for visitors), `false`, `null`, or
 * the string "true". Before the user payload has arrived the answer is `pending`, never
 * `allowed`, so the gate renders nothing rather than flashing trading at someone who may not be
 * allowed to see it.
 *
 * WHERE IT IS ENFORCED. Every page under `src/pages/trading` exports `withTradingGate(Page)`,
 * which mounts the page only when the answer is `allowed` (so no trading component, and no
 * Vertex request, exists before that), sends `denied` to the home page, and renders nothing
 * while `pending`. Outside the trading pages, the Trading menu entry and the header's
 * buying-power strip render only when `useTradingVisible()`. `e2e/tests/trading-gate.spec.ts`
 * tests this rule case by case and audits the source for any surface that skips it, and
 * `.eslintrc` (`no-restricted-imports`) fails `next build` if shared code imports a trading
 * module other than `useTradingAccess`.
 *
 * HOW IT IS CONFIGURED TODAY. The Vercel dashboard, not this repo, holds the switch, so check it
 * there after any change: `NEXT_PUBLIC_TRADING_ENABLED=1` and `NEXT_PUBLIC_API_URL_VERTEX` are set
 * on the Preview environment for all branches, and Production has neither. A beta shows trading
 * only once its TED backend (ted-devN) sends `trading_platform_access`; an older TED leaves the
 * key out, which reads as denied. MR previews compile trading, but Vertex nonprod's CORS admits
 * only the beta and sandbox hosts, so their Vertex calls fail. After a master deploy, app.ortex.com/trading
 * must answer 404 and the client bundle must contain no Vertex strings (`vertex_csrftoken`,
 * `/interface/api/alpaca`).
 * Grants are made in the TED user admin (`PRODUCT_TRADING_PLATFORM`); while TED requires it,
 * the person must also be staff. Revoking is not instant: Vertex caches TED's answer for 300 s
 * per email in each worker process, and an open tab keeps the payload it loaded until reload.
 *
 * WHAT THIS IS NOT. This decides what the GUI shows. It is not the brokerage's security
 * boundary: Vertex enforces the same TED answer on its own API (sign-in and every request,
 * vertex#96), so a user calling Vertex directly is refused there.
 *
 * This module is deliberately free of React, Next and path aliases so the rule can be tested
 * on its own (`e2e/tests/trading-gate.spec.ts`).
 */

export type TradingAccess = "pending" | "allowed" | "denied";

/** The server-provided fact the rule reads from the `/API/v2/user` payload. */
export interface TradingAccessUser {
    /** TED's combined answer (group, plus staff while TED requires it). */
    trading_platform_access?: boolean;
}

export interface TradingAccessInput {
    /** `process.env.NEXT_PUBLIC_TRADING_ENABLED === "1"`. */
    buildEnabled: boolean;
    /** `isOrtexApp()`: the Capacitor iOS/Android shell. */
    nativeApp: boolean;
    /** The `/API/v2/user` payload, or null/undefined before it has arrived. */
    userData: TradingAccessUser | null | undefined;
}

/** The build-time switch. Read once; Next inlines `NEXT_PUBLIC_*` at build time. */
export const TRADING_BUILD_ENABLED = process.env.NEXT_PUBLIC_TRADING_ENABLED === "1";

/**
 * The rule above, as a pure function. Checks run cheapest and least user-dependent first, so a
 * build without trading never even waits for the user payload.
 */
export function tradingAccess({ buildEnabled, nativeApp, userData }: TradingAccessInput): TradingAccess {
    if (buildEnabled !== true) return "denied";
    if (nativeApp) return "denied";
    if (!userData) return "pending";
    // Strict `=== true`: a truthy non-boolean (the string "true", 1) is not consent, and a
    // backend that predates the key (no `trading_platform_access`) is a no.
    if (userData.trading_platform_access !== true) return "denied";
    return "allowed";
}
